import { lazy, Suspense, useEffect, useRef } from 'react';
import { lazyRetry } from './utils/lazyRetry';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { isContractor } from './utils/permissions';
import { LEAVE_MANAGEMENT_ENABLED } from './constants';
import { Box, StyledEngineProvider } from '@mui/material';
import theme from './theme';
import LoadingSpinner from './components/common/LoadingSpinner';
import ErrorBoundary from './components/common/ErrorBoundary';
import Footer from './components/common/Footer/Footer';
import { ComingSoon } from './components/common';
import {
  SettingsOutlined as SettingsIcon,
  DateRangeOutlined as LeaveIcon,
} from '@mui/icons-material';
import Login from './pages/Login';
import { ScrollContainerContext } from './contexts/ScrollContainerContext';

// Lazy load components
const Dashboard = lazy(() => import('./pages/Dashboard'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const Announcements = lazy(() => import('./pages/People/Announcements'));
const EmployeeDirectory = lazy(
  () => import('./pages/People/EmployeeDirectory')
);
const UserDirectory = lazy(() => import('./pages/People/UserDirectory'));
const Policies = lazy(() => import('./pages/HR/Policies'));
const Performance = lazy(() => import('./pages/HR/Performance'));
const FinalRatings = lazy(() => import('./pages/HR/FinalRatings'));
const Goals = lazy(() => import('./pages/HR/Goals'));
const Attendance = lazyRetry(() => import('./pages/attendance/Holidays'));
const RoleManagement = lazy(() => import('./pages/Management/RoleManagement'));
const Navbar = lazy(() => import('./components/Navbar'));
// Coming Soon pages
const OrgChart = lazy(() => import('./pages/People/OrgChart'));
const Projects = lazy(() => import('./pages/People/Projects'));
const ProjectDetail = lazy(() => import('./pages/People/ProjectDetail'));
const Surveys = lazy(() => import('./pages/Surveys'));
const SurveyHistory = lazy(() => import('./pages/SurveyHistory'));
const Certifications = lazy(() => import('./pages/Certifications'));
const Rewards = lazy(() => import('./pages/Rewards'));
const Leave = lazy(() => import('./pages/Management/Leave'));
const Recruitment = lazy(() => import('./pages/Management/Recruitment'));
const AssetManagement = lazy(
  () => import('./pages/Management/AssetManagement')
);
const Expenses = lazy(() => import('./pages/Management/Expenses'));
const Timesheet = lazy(() => import('./pages/attendance/Timesheet'));
// Email deep-link resolver (e.g. /r/expense/123 → opens the request modal).
const RequestRedirect = lazy(() => import('./pages/RequestRedirect'));
// Email deep-link resolver for Project Management (e.g. /r/project/12)
const ProjectRedirect = lazy(() => import('./pages/ProjectRedirect'));
const PrivateRoute = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  if (!isAuthenticated) {
    // Remember where the user was headed so we can return there after login
    // (covers email deep links opened while logged out).
    const target = `${location.pathname}${location.search}`;
    if (target && !target.startsWith('/login')) {
      sessionStorage.setItem('postLoginRedirect', target);
    }
    return <Navigate to="/login" />;
  }
  return children;
};
const AdminRoute = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  const isAdmin = user?.roles?.includes('SUPER_ADMIN');
  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }
  if (!isAdmin) {
    return <Navigate to="/dashboard" />;
  }
  return children;
};

// Contractors have no leave entitlement — block the Leave Management module.
const LeaveRoute = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }
  if (isContractor(user)) {
    return <Navigate to="/dashboard" />;
  }
  return children;
};

const MainLayout = ({ children }) => {
  const location = useLocation();
  const mainContentRef = useRef(null);

  useEffect(() => {
    // Scroll window to top
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'instant',
    });

    // Also scroll the main content container
    if (mainContentRef.current) {
      // Use requestAnimationFrame to ensure it happens after render
      requestAnimationFrame(() => {
        if (mainContentRef.current) {
          mainContentRef.current.scrollTo({
            top: 0,
            left: 0,
            behavior: 'instant',
          });
        }
      });
    }
  }, [location.pathname]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <Suspense fallback={<LoadingFallback />}>
        <Navbar />
      </Suspense>
      <Box
        ref={mainContentRef}
        component="main"
        sx={{
          flexGrow: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          height: '100vh',
          pt: '64px',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          '&::-webkit-scrollbar': {
            display: 'none',
          },
        }}
      >
        <ScrollContainerContext.Provider value={mainContentRef}>
          <Suspense fallback={<LoadingFallback />}>{children}</Suspense>
        </ScrollContainerContext.Provider>
      </Box>
      <Footer />
    </Box>
  );
};

const routes = [
  {
    path: '/login',
    element: <Login />,
    public: true,
    layout: null, // No layout wrapper - Login component has its own styling
  },
  {
    path: '/dashboard',
    element: <Dashboard />,
    public: false,
    layout: MainLayout,
  },
  {
    path: '/admin/dashboard',
    element: (
      <AdminRoute>
        <AdminDashboard />
      </AdminRoute>
    ),
    public: false,
    layout: MainLayout,
  },
  {
    path: '/people/announcements',
    element: <Announcements />,
    public: false,
    layout: MainLayout,
  },
  {
    path: '/people/employee-directory',
    element: <EmployeeDirectory />,
    public: false,
    layout: MainLayout,
  },
  {
    path: '/people/user-directory',
    element: <UserDirectory />,
    public: false,
    layout: MainLayout,
  },
  {
    path: '/management/policies',
    element: <Policies />,
    public: false,
    layout: MainLayout,
  },
  {
    path: '/management/performance-management',
    element: <Performance />,
    public: false,
    layout: MainLayout,
  },
  {
    path: '/hr/final-ratings',
    element: <FinalRatings />,
    public: false,
    layout: MainLayout,
  },
  {
    path: '/hr/goals',
    element: <Goals />,
    public: false,
    layout: MainLayout,
  },
  {
    path: '/attendance/holidays',
    element: <Attendance />,
    public: false,
    layout: MainLayout,
  },
  {
    path: '/management/role-management',
    element: <RoleManagement />,
    public: false,
    layout: MainLayout,
  },
  {
    path: '/management/assets',
    element: <AssetManagement />,
    // element: (
    //   <ComingSoon
    //     title="Asset Management"
    //     description="Track and manage company assets including equipment, software licenses, and office supplies. This feature will include asset inventory, maintenance schedules, and allocation tracking."
    //     icon={SettingsIcon}
    //   />
    // ),
    public: false,
    layout: MainLayout,
  },
  // Coming Soon pages
  {
    path: '/people/org-chart',
    element: <OrgChart />,
    public: false,
    layout: MainLayout,
  },
  {
    path: '/management/projects',
    element: <Projects />,
    public: false,
    layout: MainLayout,
  },
  {
    path: '/management/projects/:projectId',
    element: <ProjectDetail />,
    public: false,
    layout: MainLayout,
  },
  {
    path: '/surveys',
    element: <Surveys />,
    public: false,
    layout: MainLayout,
  },
  {
    path: '/survey-history',
    element: <SurveyHistory />,
    public: false,
    layout: MainLayout,
  },
  {
    path: '/certifications',
    element: <Certifications />,
    public: false,
    layout: MainLayout,
  },
  {
    path: '/rewards',
    element: <Rewards />,
    public: false,
    layout: MainLayout,
  },
  {
    path: '/management/leave',
    // Leave Management isn't live yet — show a Coming Soon screen instead of
    // the module. Flip LEAVE_MANAGEMENT_ENABLED to restore the real page.
    element: LEAVE_MANAGEMENT_ENABLED ? (
      <LeaveRoute>
        <Leave />
      </LeaveRoute>
    ) : (
      <ComingSoon
        title="Leave Management"
        description="Request, approve, and track employee leave requests. This feature will include leave balances, approval workflows, calendar integration, and leave policies."
        icon={LeaveIcon}
      />
    ),
    public: false,
    layout: MainLayout,
  },
  {
    path: '/management/recruitment',
    element: <Recruitment />,
    public: false,
    layout: MainLayout,
  },
  {
    path: '/management/expenses',
    element: <Expenses />,
    public: false,
    layout: MainLayout,
  },
  {
    path: '/attendance/timesheet',
    element: <Timesheet />,
    public: false,
    layout: MainLayout,
  },
  {
    // Deep link into the Timesheet page's "My Requests" tab for one
    // submission — canonical URL (stays in the address bar), not a
    // redirect-and-swap like /r/:type/:id.
    path: '/myRequest/:submissionId',
    element: <Timesheet />,
    public: false,
    layout: MainLayout,
  },
  {
    // Deep link into the Timesheet page's "Approvals" tab for one
    // submission — same canonical-URL model as /myRequest/:submissionId.
    path: '/myApprovals/:submissionId',
    element: <Timesheet />,
    public: false,
    layout: MainLayout,
  },
  {
    // Project Management deep link: /r/project/:projectId → the project's
    // own Details page. More specific than /r/:type/:id below (a literal
    // "project" segment outranks :type at the same position), so this wins
    // for that shape regardless of array order.
    path: '/r/project/:projectId',
    element: <ProjectRedirect />,
    public: false,
    layout: null,
  },
  {
    // Project Management deep link: /r/project/:projectId/:itemType/:itemId
    // (itemType: sprint | user-story | task | bug) → that item's own edit
    // view, inside the project.
    path: '/r/project/:projectId/:itemType/:itemId',
    element: <ProjectRedirect />,
    public: false,
    layout: null,
  },
  {
    // Invoice deep link: /r/invoice/:projectId/:invoiceId → All Invoices for
    // Accounts Admin / Super Admin, else the project's Invoice tab. Outranks
    // /r/:type/:audience/:id via the literal "invoice" segment.
    path: '/r/invoice/:projectId/:invoiceId',
    element: <ProjectRedirect isInvoice />,
    public: false,
    layout: null,
  },
  {
    // Deep-link resolver (legacy, no audience → reporting manager):
    // /r/:type/:id → the request's page + Approvals tab.
    path: '/r/:type/:id',
    element: <RequestRedirect />,
    public: false,
    layout: null,
  },
  {
    // Audience-aware deep link: /r/:type/:audience/:id → the screen where that
    // role acts on the request (employee | manager | hr | accounts | it | admin).
    path: '/r/:type/:audience/:id',
    element: <RequestRedirect />,
    public: false,
    layout: null,
  },
];
// Loading component
const LoadingFallback = () => (
  <LoadingSpinner fullScreen message="Loading application..." />
);
function App() {
  return (
    <ErrorBoundary>
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <AuthProvider>
            <Router>
              <Suspense fallback={<LoadingFallback />}>
                <Routes>
                  {routes.map(
                    ({ path, element, public: isPublic, layout: Layout }) => (
                      <Route
                        key={path}
                        path={path}
                        element={
                          isPublic ? (
                            Layout ? (
                              <Layout>{element}</Layout>
                            ) : (
                              element
                            )
                          ) : (
                            <PrivateRoute>
                              {Layout ? <Layout>{element}</Layout> : element}
                            </PrivateRoute>
                          )
                        }
                      />
                    )
                  )}
                  <Route path="/" element={<Navigate to="/dashboard" />} />
                </Routes>
              </Suspense>
            </Router>
          </AuthProvider>
        </ThemeProvider>
      </StyledEngineProvider>
    </ErrorBoundary>
  );
}
export default App;
